import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { loadSavedForm, saveForm } from './formUtils';

const emptyDir = () => ({ _uid: crypto.randomUUID(), fullName: '', idNumber: '', email: '', phone: '' });

const DEFAULTS = {
  companyName: '',
  registrationNumber: '',
  changeType: 'add',
  directorsToAdd: [emptyDir()],
  directorsToRemove: [emptyDir()],
  effectiveDate: '',
  reason: '',
};

const steps = ['Company', 'Changes', 'Review'];

const DirectorChangesForm = ({ serviceId, onComplete }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => loadSavedForm(serviceId) || DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveForm(serviceId, form);
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const updateArr = (key, i, field, value) =>
    setForm((p) => ({
      ...p,
      [key]: p[key].map((r, idx) => (idx === i ? { ...r, [field]: value } : r)),
    }));
  const addRow = (key) =>
    setForm((p) => ({ ...p, [key]: [...p[key], emptyDir()] }));
  const removeRow = (key, i) =>
    setForm((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));

  const validate = () => {
    if (step === 1) {
      if (!form.companyName || !form.registrationNumber) {
        toast({
          title: 'Required Fields Missing',
          description: 'Please provide the company name and registration number.',
          variant: 'destructive',
        });
        return false;
      }
    }
    if (step === 2) {
      const needAdd = form.changeType === 'add' || form.changeType === 'both';
      const needRemove = form.changeType === 'remove' || form.changeType === 'both';
      if (needAdd && form.directorsToAdd.some((d) => !d.fullName || !d.idNumber)) {
        toast({
          title: 'Required Fields Missing',
          description: 'Complete name & ID for every director to add.',
          variant: 'destructive',
        });
        return false;
      }
      if (needRemove && form.directorsToRemove.some((d) => !d.fullName || !d.idNumber)) {
        toast({
          title: 'Required Fields Missing',
          description: 'Complete name & ID for every director to remove.',
          variant: 'destructive',
        });
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validate()) return;
    onComplete(form);
  };

  const renderDirectorList = (key, title, color) => (
    <div>
      <h4 className="font-semibold text-gray-800 mb-2">{title}</h4>
      {form[key].map((d, i) => (
        <Card key={d._uid || `${key}-${i}`} className="mb-3">
          <CardHeader className={color}>
            <div className="flex justify-between items-center">
              <CardTitle className="text-base">
                {title.replace(/s$/, '')} {i + 1}
              </CardTitle>
              {form[key].length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRow(key, i)}
                  className="text-red-600"
                >
                  Remove
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={d.fullName}
                  onChange={(e) => updateArr(key, i, 'fullName', e.target.value)}
                />
              </div>
              <div>
                <Label>ID Number *</Label>
                <Input
                  value={d.idNumber}
                  onChange={(e) => updateArr(key, i, 'idNumber', e.target.value)}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={d.email}
                  onChange={(e) => updateArr(key, i, 'email', e.target.value)}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={d.phone}
                  onChange={(e) => updateArr(key, i, 'phone', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      <Button
        onClick={() => addRow(key)}
        variant="outline"
        className="w-full border-dashed border-2 border-emerald-300 text-emerald-600 hover:bg-emerald-50"
      >
        + Add Another
      </Button>
    </div>
  );

  return (
    <FormShell
      title="Director / Shareholder Changes"
      stepLabels={steps}
      currentStep={step}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={saved}
    >
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Company Information</h3>
          <div>
            <Label>Registered Company Name *</Label>
            <Input
              value={form.companyName}
              onChange={(e) => set('companyName', e.target.value)}
            />
          </div>
          <div>
            <Label>CIPC Registration Number *</Label>
            <Input
              value={form.registrationNumber}
              onChange={(e) => set('registrationNumber', e.target.value)}
            />
          </div>
          <div>
            <Label>Effective Date</Label>
            <Input
              type="date"
              value={form.effectiveDate}
              onChange={(e) => set('effectiveDate', e.target.value)}
            />
          </div>
          <div>
            <Label>Change Type</Label>
            <select
              value={form.changeType}
              onChange={(e) => set('changeType', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="add">Add director(s)</option>
              <option value="remove">Remove director(s)</option>
              <option value="both">Add &amp; Remove</option>
            </select>
          </div>
          <div>
            <Label>Reason / Notes</Label>
            <textarea
              value={form.reason}
              onChange={(e) => set('reason', e.target.value)}
              className="mt-1 w-full p-2 border border-gray-300 rounded-md min-h-[80px]"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          {(form.changeType === 'add' || form.changeType === 'both') &&
            renderDirectorList('directorsToAdd', 'Directors to Add', 'bg-emerald-50')}
          {(form.changeType === 'remove' || form.changeType === 'both') &&
            renderDirectorList('directorsToRemove', 'Directors to Remove', 'bg-red-50')}
        </div>
      )}

      {step === 3 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-800">Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Company:</strong> {form.companyName}</p>
            <p><strong>Reg No:</strong> {form.registrationNumber}</p>
            <p><strong>Change Type:</strong> {form.changeType}</p>
            {(form.changeType === 'add' || form.changeType === 'both') && (
              <p><strong>Adding:</strong> {form.directorsToAdd.length} director(s)</p>
            )}
            {(form.changeType === 'remove' || form.changeType === 'both') && (
              <p><strong>Removing:</strong> {form.directorsToRemove.length} director(s)</p>
            )}
            <p><strong>Effective:</strong> {form.effectiveDate || 'ASAP'}</p>
          </CardContent>
        </Card>
      )}
    </FormShell>
  );
};

export default DirectorChangesForm;
